// Phase 6 — Page Content Manager schema.
// This is the only allowed surface area for content overrides.
// Add new fields here to expose them in /admin/pages.

export type FieldType = "text" | "textarea" | "url" | "image_url";

export interface FieldDef {
  field_key: string;
  label: string;
  type: FieldType;
  helper?: string;
  placeholder?: string;
}

export interface SectionDef {
  section_key: string;
  label: string;
  fields: FieldDef[];
}

export interface PageDef {
  page_key: string;
  label: string;
  sections: SectionDef[];
}

const ctaUrlHelper = "Internal path (/...) or full URL (https://...)";

export const PAGE_CONTENT_SCHEMA: PageDef[] = [
  {
    page_key: "home",
    label: "Homepage",
    sections: [
      {
        section_key: "hero",
        label: "Hero",
        fields: [
          { field_key: "title", label: "Title", type: "text" },
          { field_key: "subtitle", label: "Subtitle", type: "textarea" },
          { field_key: "primary_cta_label", label: "Primary CTA label", type: "text" },
          { field_key: "primary_cta_url", label: "Primary CTA URL", type: "url", helper: ctaUrlHelper },
          { field_key: "secondary_cta_label", label: "Secondary CTA label", type: "text" },
          { field_key: "secondary_cta_url", label: "Secondary CTA URL", type: "url", helper: ctaUrlHelper },
        ],
      },
      {
        section_key: "mission",
        label: "Mission / Approach",
        fields: [
          { field_key: "heading", label: "Heading", type: "text" },
          { field_key: "body", label: "Body", type: "textarea" },
        ],
      },
      {
        section_key: "final_cta",
        label: "Final CTA",
        fields: [
          { field_key: "heading", label: "Heading", type: "text" },
          { field_key: "body", label: "Body", type: "textarea" },
          { field_key: "button_label", label: "Button label", type: "text" },
          { field_key: "button_url", label: "Button URL", type: "url", helper: ctaUrlHelper },
        ],
      },
    ],
  },
  {
    page_key: "about",
    label: "About",
    sections: [
      {
        section_key: "hero",
        label: "Hero",
        fields: [
          { field_key: "title", label: "Title", type: "text" },
          { field_key: "subtitle", label: "Subtitle/Eyebrow", type: "text" },
        ],
      },
      {
        section_key: "intro",
        label: "Intro / Overview",
        fields: [
          { field_key: "heading", label: "Heading", type: "text" },
          { field_key: "body", label: "Body", type: "textarea" },
        ],
      },
      {
        section_key: "standard",
        label: "REALTORS® Standard",
        fields: [
          { field_key: "heading", label: "Heading", type: "text" },
          { field_key: "body", label: "Body", type: "textarea" },
        ],
      },
      {
        section_key: "final_cta",
        label: "Final CTA",
        fields: [
          { field_key: "heading", label: "Heading", type: "text" },
          { field_key: "body", label: "Body", type: "textarea" },
          { field_key: "button_label", label: "Button label", type: "text" },
          { field_key: "button_url", label: "Button URL", type: "url", helper: ctaUrlHelper },
        ],
      },
    ],
  },
  {
    page_key: "dr_investing",
    label: "Dominican Republic Investing",
    sections: [
      {
        section_key: "hero",
        label: "Hero",
        fields: [
          { field_key: "title", label: "Title", type: "text" },
          { field_key: "subtitle", label: "Subtitle", type: "text" },
        ],
      },
      {
        section_key: "intro",
        label: "Intro",
        fields: [
          { field_key: "heading", label: "Heading", type: "text" },
          { field_key: "body", label: "Body", type: "textarea" },
        ],
      },
      {
        section_key: "final_cta",
        label: "Final CTA",
        fields: [
          { field_key: "heading", label: "Heading", type: "text" },
          { field_key: "body", label: "Body", type: "textarea" },
          { field_key: "button_label", label: "Button label", type: "text" },
          { field_key: "button_url", label: "Button URL", type: "url", helper: ctaUrlHelper },
        ],
      },
    ],
  },
  {
    page_key: "consultation_cta",
    label: "Contact / Consultation CTA",
    sections: [
      {
        section_key: "consultation",
        label: "Consultation CTA",
        fields: [
          { field_key: "heading", label: "Heading", type: "text" },
          { field_key: "body", label: "Body", type: "textarea" },
          { field_key: "button_label", label: "Button label", type: "text" },
          { field_key: "button_url", label: "Button URL", type: "url", helper: ctaUrlHelper },
        ],
      },
    ],
  },
];

export function isValidUrlOrPath(v: string): boolean {
  const t = v.trim();
  if (!t) return true; // empty allowed (treated as no override)
  if (t.startsWith("/") || t.startsWith("#")) return true;
  try {
    new URL(t);
    return true;
  } catch {
    return false;
  }
}
